import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { yellow } from '@mui/material/colors';
import CreateProductWoo from '../../CreateProductWoo';

const DraftProductCard = ({
  ciornaPlatforma,
  onChangeCiornaPlatforma,
  onSaveCiornaPlatforma,
  customFields,
  onFilter,
  filteredProducts,
  filteredProductsLoading = false,
  filteredProductsError = "",
  selectedProductIds,
  onSelectProduct,
  showCreateProduct,
  productData,
  onCreateDraftClick,
  isCreatingDraft = false,
}) => {
  const cleanProductName = (product) =>
    product.cleanName ||
    String(product.name || "")
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const matchChipColor = (percent) => {
    if (percent >= 95) return "success";
    if (percent >= 90) return "primary";
    if (percent >= 80) return "warning";
    return "default";
  };

  const ciornaSku = ciornaPlatforma === "Not available" ? "" : ciornaPlatforma;
  const criteria = [
    { label: "Brand", value: customFields.brand },
    { label: "Categorie", value: customFields.categorie },
    { label: "Model", value: customFields.model },
  ];
  const selectedCount = selectedProductIds.length;

  return (
    <Card
      sx={{
        width: '100%',
        marginBottom: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
      }}
    >
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: yellow[700], color: '#000' }} aria-label="ciorna-produs">
            <Inventory2OutlinedIcon />
          </Avatar>
        }
        title={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Typography variant="h6" fontWeight={800}>
              Ciorna Produs
            </Typography>
            {selectedCount > 0 && (
              <Chip
                size="small"
                icon={<CheckCircleOutlineIcon />}
                color="success"
                label={`${selectedCount} selectat`}
              />
            )}
          </Stack>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <Stack spacing={2.5}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            <TextField
              label="SKU Ciornă (JIRA 10083)"
              value={ciornaSku}
              placeholder="SKU din JIRA (customfield_10083)"
              onChange={(e) => onChangeCiornaPlatforma(e.target.value)}
              variant="outlined"
              fullWidth
              size="small"
            />
            <Button
              variant="outlined"
              startIcon={<SaveOutlinedIcon />}
              onClick={onSaveCiornaPlatforma}
              sx={{ minHeight: 40, fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              Salvează SKU
            </Button>
          </Box>

          <Divider />

          <Box>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography variant="subtitle1" fontWeight={800}>
                Potrivire produs sursă
              </Typography>
              <Button
                variant="contained"
                startIcon={filteredProductsLoading ? <CircularProgress size={16} color="inherit" /> : <SearchOutlinedIcon />}
                onClick={onFilter}
                disabled={filteredProductsLoading}
                sx={{ bgcolor: '#2563eb', fontWeight: 800, '&:hover': { bgcolor: '#1d4ed8' } }}
              >
                {filteredProductsLoading ? "Se caută..." : "Caută produse"}
              </Button>
            </Stack>

            <Grid container spacing={1.5}>
              {criteria.map((item) => (
                <Grid item xs={12} md={4} key={item.label}>
                  <TextField
                    label={item.label}
                    value={item.value || ""}
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              ))}
            </Grid>

            {filteredProductsError && (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                {filteredProductsError}
              </Alert>
            )}

            <Box sx={{ mt: 2, maxHeight: 460, overflowY: 'auto', pr: { md: 1 } }}>
              {filteredProducts.length === 0 && !filteredProductsLoading ? (
                <Alert severity="info">Nu există produse propuse pentru criteriile curente.</Alert>
              ) : (
                <Grid container spacing={1.5}>
                  {filteredProducts.map((product) => {
                    const isSelected = selectedProductIds.includes(product.sku);
                    const productName = cleanProductName(product);
                    return (
                      <Grid item xs={12} sm={6} lg={4} key={product.sku || product.id}>
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={() => onSelectProduct(product.sku)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onSelectProduct(product.sku);
                            }
                          }}
                          sx={{
                            height: '100%',
                            border: '1px solid',
                            borderColor: isSelected ? 'success.main' : 'divider',
                            borderRadius: 1,
                            bgcolor: isSelected ? 'rgba(46, 125, 50, 0.06)' : 'background.paper',
                            boxShadow: isSelected ? '0 8px 20px rgba(46, 125, 50, 0.12)' : '0 4px 14px rgba(15, 23, 42, 0.06)',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                            '&:hover': {
                              borderColor: isSelected ? 'success.main' : 'primary.main',
                              boxShadow: '0 10px 26px rgba(15, 23, 42, 0.12)',
                              transform: 'translateY(-1px)',
                            },
                            '&:focus-visible': {
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                              outlineOffset: 2,
                            },
                          }}
                        >
                          <Box sx={{ position: 'relative', bgcolor: 'grey.100' }}>
                            <CardMedia
                              component="img"
                              image={product.df_image_link}
                              alt={productName}
                              sx={{
                                width: '100%',
                                height: { xs: 220, sm: 240 },
                                objectFit: 'cover',
                                display: 'block',
                              }}
                            />
                            <Tooltip title={isSelected ? "Deselectează" : "Selectează"}>
                              <Checkbox
                                checked={isSelected}
                                onClick={(event) => event.stopPropagation()}
                                onChange={() => onSelectProduct(product.sku)}
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  left: 8,
                                  bgcolor: 'rgba(255, 255, 255, 0.92)',
                                  borderRadius: 1,
                                  p: 0.5,
                                  '&:hover': { bgcolor: '#fff' },
                                }}
                              />
                            </Tooltip>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              sx={{
                                position: 'absolute',
                                right: 8,
                                bottom: 8,
                                flexWrap: 'wrap',
                                justifyContent: 'flex-end',
                                rowGap: 0.75,
                              }}
                            >
                              {Number.isFinite(Number(product.matchPercent)) && (
                                <Chip
                                  size="small"
                                  color={matchChipColor(Number(product.matchPercent))}
                                  label={`${Number(product.matchPercent)}%`}
                                  sx={{ fontWeight: 800, bgcolor: 'background.paper' }}
                                />
                              )}
                              {product.matchBucket && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`Prag ${product.matchBucket}%`}
                                  sx={{ bgcolor: 'background.paper' }}
                                />
                              )}
                            </Stack>
                          </Box>
                          <Box sx={{ p: 1.5, minWidth: 0 }}>
                            <Typography
                              fontWeight={800}
                              sx={{
                                minHeight: 48,
                                wordBreak: 'break-word',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {productName}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                              {product.sku && <Chip size="small" label={product.sku} sx={{ fontWeight: 700 }} />}
                              {isSelected && <Chip size="small" color="success" icon={<CheckCircleOutlineIcon />} label="Selectat" />}
                            </Stack>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>

            {selectedCount > 0 && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={isCreatingDraft ? <CircularProgress size={16} color="inherit" /> : <AddCircleOutlineIcon />}
                  onClick={onCreateDraftClick}
                  disabled={isCreatingDraft}
                  sx={{
                    minHeight: 44,
                    px: 3,
                    bgcolor: '#111827',
                    color: '#fff',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#1f2937' },
                  }}
                >
                  {isCreatingDraft ? "Se creează..." : "Creează Ciornă"}
                </Button>
              </Box>
            )}
          </Box>

          {showCreateProduct && productData && <CreateProductWoo productData={productData} />}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DraftProductCard;
